import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { OrderMeal, Menu, OrderMealEntree, BasicOrderMealItem, OrderType, OrderErrorCode, OrderApiException, OrderMealItem, MealType, BYOMealType, NonFoodItem, OrderWithContext } from '@/models';
import { isMealBYOProtein, makeFormField, removeLargeNumbers } from '@/helpers';
import { Routes, isGroupOrderRoute } from '@/router';
import { RemoveFromBagEvent } from '@/services/analytics/events/remove-from-bag-event';
import { BagClickEvent } from '@/services/analytics/events';
import { COUNTRIES_TAX_INCLUSIVE } from '@/store/ui';
import { MaxEntreeErrorEvent } from '@/services/analytics/events/max-byoc-error-event';
import { BYOFM_BOWLS_MENU_ID, BYOFM_SERVING_UTENSILS_MENU_ID } from '@/constants';

@Component({
  name: 'CmgMealCard',
})
export default class CmgMealCard extends Vue {
  isEditing = false;
  isSavingEditedName = false;
  editedMealName = makeFormField();

  @Prop({ required: true })
  public meal!: OrderMeal;

  @Prop({ default: true, required: false })
  public editable!: boolean;

  get isTaxInclusiveCountry(): boolean {
    const taxInclusiveIndex = COUNTRIES_TAX_INCLUSIVE.findIndex(item => new RegExp(this.$appStore.state.ui.countryProfile.countryCode, 'i').test(item));
    return taxInclusiveIndex >= 0;
  }

  get mealPrice(): string {
    return this.$services.utils.formatPrice(this.isTaxInclusiveCountry ? (this.meal.mealTotalAmount + this.meal.mealDiscountAmount) : this.meal.mealExtendedPrice);
  }

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get entrees(): OrderMealEntree[] {
    return this.meal.entrees || [];
  }

  get entreeName() {
    return this.entrees[0]?.preconfiguredItemName ?? this.entrees[0]?.menuItemName ?? '';
  }

  get sidesAndDrinks(): BasicOrderMealItem[] {
    return (this.meal.sides || []).concat(this.meal.drinks || []);
  }

  get isDisabled(): boolean {
    const customerOwnsMeal = this.$services.order.isCustomerParticipantOfGroupOrder() ? this.$appStore.state.order.groupOrderMealIds.indexOf(this.meal.mealId) > -1 : true;

    return !this.editable || !customerOwnsMeal;
  }

  get orderNonFoodItems(): NonFoodItem[] {
    const currentOrder = this.$store.getters.currentOrder;
    return (currentOrder && currentOrder.order.nonFoodItems) || [];
  }

  mounted() {
    this.editedMealName.value = this.meal.mealName;
  }

  formatPrice(item: OrderMealItem): string {
    return this.$services.utils.formatPrice(this.isTaxInclusiveCountry ? (item.itemTotalAmount + item.itemDiscountAmount) : item.itemExtendedPrice);
  }

  onMealNameFocus() {
    this.isEditing = true;

    this.$emit('request-meal-edit', this.meal);
  }

  async onDoneEditing() {
    if (this.editedMealName.error) {
      return;
    }

    const order = this.$services.order.getCurrentOrder();
    if (!order) {
      return;
    }

    if (this.editedMealName.value != this.meal.mealName) {
      this.isSavingEditedName = true;
      try {
        await this.$services.order.updateMealName(order, this.meal.mealId, this.editedMealName.value);
      } finally {
        this.isSavingEditedName = false;
      }
    }

    const inputComponent = this.$refs['meal-name-input'] as Vue;
    // inputComponent will be undefined if the user navigates away from the bag
    if (inputComponent) {
      (inputComponent.$el as HTMLElement).blur();
    }

    this.isEditing = false;
    this.$emit('meal-edit-complete');
    Vue.nextTick(() => {
      setTimeout(() => {
        this.$services.ui.nextFocus('current ' + this.meal.mealId);
      });
    });
  }

  validateEditedMealName() {
    if (!this.editedMealName.value) {
      this.editedMealName.error = `Who's this meal for?`;
    }
    else {
      this.editedMealName.error = null;
    }
  }

  removeLargeNumbersEditedMealName(){
    const editedMealName1 = this.editedMealName;
    let editedMealNameClean = removeLargeNumbers(editedMealName1.value);
    this.editedMealName.value = editedMealNameClean;
  }

  sanitizeEditedMealName(value: string, oldValue: string) {
    return this.isEditing ? value.trimLeft().slice(0, 20) : oldValue;
  }

  get shouldShowDuplicateBtn(): boolean {
    return !this.$services.order.isCustomerParticipantOfGroupOrder();
  }

  get shouldShowActions(): boolean {
    if (!this.editable) {
      return false;
    }

    const order = this.$services.order.getCurrentOrder();
    if (!order) {
      return false;
    }

    if (order.order.orderType != OrderType.Group) {
      return true;
    }

    return this.$services.order.isCustomerHostOfGroupOrder() || this.$appStore.state.order.groupOrderMealIds.indexOf(this.meal.mealId) > -1;
  }

  async onClickRemove(): Promise<void> {
    const order = this.$services.order.getCurrentOrder();
    if (!order) {
      return;
    }

    this.$services.ui.incrementLoader('bag');
    try {
      await this.$services.order.removeMealFromOrder(order, this.meal.mealId);
      await this.removeNonFoodItemsIfBYOMeal(order);

      this.$emit('meal-removed'); //let the parent know we're removing it while its still the parent
      this.updateDeliveryEstimate();
      this.$services.analytics.send(new RemoveFromBagEvent(this.meal));

      if (this.$services.order.isCustomerParticipantOfGroupOrder()) {
        this.$services.order.clearGroupParticipantMeal();
        this.$router.push({ name: Routes.GroupOrderMenu });
        this.$services.bag.setBagVisibility(false);
      }
    } catch {
      // TODO-x9qax2: Handle error
    } finally {
      this.$services.ui.decrementLoader('bag');
    }
  }

  async removeNonFoodItemsIfBYOMeal(order: OrderWithContext): Promise<void> { 
    const byofmBowlsInOrder = this.orderNonFoodItems.find(i => i.menuItemId == BYOFM_BOWLS_MENU_ID);
    if (isMealBYOProtein(this.meal, this.$appStore.state.menu.activeMenu) && byofmBowlsInOrder) {
      await this.$services.order.removeNonFoodItemFromOrder(order, byofmBowlsInOrder.nonFoodItemId);
    }

    const byofmServingUtensils = this.orderNonFoodItems.find(i => i.menuItemId == BYOFM_SERVING_UTENSILS_MENU_ID);
    if (isMealBYOProtein(this.meal, this.$appStore.state.menu.activeMenu) && byofmServingUtensils) {
      await this.$services.order.removeNonFoodItemFromOrder(order, byofmServingUtensils.nonFoodItemId);
    }
  }

  onClickEdit(event: any): void {
    let isUneditedPCE = this.meal.preconfiguredMealId && !this.meal.wasMealModified;
    let matchingPCM = this.$appStore.state.menu.preConfiguredMeals.find(m => m.mealId == this.meal.preconfiguredMealId);
    let isBuildYourOwn = isUneditedPCE && matchingPCM?.mealType == MealType.BuildYourOwn;
    let hasEntree = matchingPCM?.entree != null && matchingPCM?.entree.itemId != null; 
    // Do not show customers an unecessary 'Changing It Up' modal for BYO
    if (isUneditedPCE && !isBuildYourOwn && hasEntree) {
      this.$services.ui.pushEventTargetAsLastSelectedElement(event);
      this.$services.ui.showMakeItMyOwnModal(this.meal.preconfiguredMealName || '', this.navigateToMealBuilderEdit);
      this.$nextTick(() => this.$services.ui.focusDiv('.cmg-modal-container'));
    } else {
      this.navigateToMealBuilderEdit();
    }

    this.$services.analytics.send(new BagClickEvent('cartEdits', this.meal));
  }

  async onClickDuplicate(event: any): Promise<void> {
    const order = this.$services.order.getCurrentOrder();
    if (!order) {
      return;
    }
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('mealBuilderMealName', true, {
      beforeClose: async mealName => {
        this.meal.mealName = mealName;
        try {
          await this.$services.order.duplicateMeal(order, this.meal);
          this.updateDeliveryEstimate();
        } catch (e: any) {
          if (e instanceof OrderApiException && (e.errors[0].errorCode == OrderErrorCode.MaxEntreeLimitExceeded || e.errors[0].errorCode == OrderErrorCode.MaxBYOLimitExceeded)) {  
            this.$services.analytics.send(
              new MaxEntreeErrorEvent(
                e.errors[0],
                this.meal.preconfiguredMealName ?? (this.meal.entrees && this.meal.entrees.length > 0 ? this.meal.entrees[0].menuItemName : ''),
                this.$router.currentRoute.name ?? '',
                isMealBYOProtein(this.meal, this.menu) ? BYOMealType.BYOProtein : BYOMealType.BYOChips
              )
            );
            this.$services.ui.setModalVisibility('maxEntreeLimitModal', true, { errorMessage: e.errors[0].errorData, errorCode: e.errors[0].errorCode });
          }
          // TODO-ftxn8r: handle error
        }
      },
      mealName: this.meal.mealName,
    });
    this.$nextTick(() => this.$services.ui.focusDiv('.cmg-modal-container'));

    this.$services.analytics.send(new BagClickEvent('orderDuplications', this.meal, 'copy-meal'));
  }

  private navigateToMealBuilderEdit(): void {
    const route = isGroupOrderRoute(this.$route) ? `/order/group/build/edit/${this.meal.mealId}` : `/order/build/edit/${this.meal.mealId}`;
    this.$router.push(route);
    this.$services.bag.setBagVisibility(false);
  }

  private updateDeliveryEstimate() {
    const estimate = this.$appStore.state.checkout.delivery.estimate;
    const order = this.$services.order.getCurrentOrder();

    if (order != null && order.order.delivery != null && estimate != null) {
      this.$services.checkout.setDeliveryEstimate({
        ...estimate,
        deliveryFee: order.order.delivery.feeAmount,
      });
    }
  }
}
